import { useState, useEffect } from 'react';
import { Navigation } from '../../components/Navigation';
import { useAuthStore } from '../../store/authStore';
import { referralService, ConnectionReason } from '../../lib/referralService';
import {
  Users,
  Copy,
  Check,
  Gift,
  TrendingUp,
  Award,
  Share2,
  DollarSign,
  Clock,
  AlertCircle,
  ShieldCheck,
  User,
} from 'lucide-react';

interface ReferralStats {
  totalReferrals: number;
  verifiedReferrals: number;
  pendingVerification: number;
  signupReferrals: number;
  subscriptionReferrals: number;
  totalCreditsEarned: number;
  pendingCredits: number;
  pendingReferrals: number;
}

interface Referral {
  id: string;
  status: string;
  created_at: string;
  signup_completed_at: string | null;
  subscription_completed_at: string | null;
  both_accounts_verified: boolean;
  referrer_account_complete: boolean;
  referee_account_complete: boolean;
  verification_check_date: string | null;
  referrer_contact: string | null;
  connection_reason: ConnectionReason | null;
  connection_reason_other: string | null;
  referee: {
    full_name: string;
    email: string;
  };
}

export function Referrals() {
  const { profile } = useAuthStore();
  const [referralCode, setReferralCode] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState<ReferralStats>({
    totalReferrals: 0,
    verifiedReferrals: 0,
    pendingVerification: 0,
    signupReferrals: 0,
    subscriptionReferrals: 0,
    totalCreditsEarned: 0,
    pendingCredits: 0,
    pendingReferrals: 0,
  });
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile) {
      loadReferralData();
    }
  }, [profile]);

  const loadReferralData = async () => {
    if (!profile) return;

    setLoading(true);
    try {
      const code = await referralService.getReferralCode(profile.id);
      setReferralCode(code || '');

      const statsData = await referralService.getReferralStats(profile.id);
      setStats(statsData);

      const referralData = await referralService.getUserReferrals(profile.id);
      setReferrals(referralData);
    } catch (error) {
      console.error('Error loading referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferralLink = () => {
    const referralLink = `${window.location.origin}/signup?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getConnectionReasonLabel = (reason: ConnectionReason | null, other?: string | null): string => {
    if (!reason) return 'Not specified';

    const labels: Record<ConnectionReason, string> = {
      friend: 'Friend',
      colleague: 'Colleague',
      family: 'Family Member',
      classmate: 'Classmate',
      mentor: 'Mentor/Mentee',
      other: other || 'Other',
    };

    return labels[reason] || 'Unknown';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Referral Program</h1>
          <p className="text-gray-600">Earn credits by inviting friends to ResuSwipe</p>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-3xl p-8 mb-8 text-white">
          <div className="flex items-center gap-3 mb-6">
            <Gift className="w-10 h-10" />
            <h2 className="text-3xl font-bold">Your Referral Code</h2>
          </div>

          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm text-white/80 mb-2">Share this code with friends</div>
                <div className="text-4xl font-bold tracking-wider">{referralCode}</div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={copyReferralCode}
                  className="px-6 py-3 bg-white text-orange-600 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg flex items-center gap-2"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
                <button
                  onClick={shareReferralLink}
                  className="px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-xl font-bold hover:bg-white/30 transition-all flex items-center gap-2"
                >
                  <Share2 className="w-5 h-5" />
                  Share Link
                </button>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <Award className="w-6 h-6" />
                <div className="text-lg font-semibold">You Earn 25 Credits</div>
              </div>
              <p className="text-sm text-white/90">When your friend signs up with your code</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <Gift className="w-6 h-6" />
                <div className="text-lg font-semibold">They Get 75 Credits</div>
              </div>
              <p className="text-sm text-white/90">Your friend gets 75 credits as signup bonus!</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-6 h-6" />
                <div className="text-lg font-semibold">You Earn 75 More</div>
              </div>
              <p className="text-sm text-white/90">When they subscribe to ResuSwipe+</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="w-8 h-8 text-green-500" />
            <h3 className="text-xl font-bold text-gray-900">Account Verification</h3>
          </div>
          <p className="text-gray-600 mb-4">
            To receive referral credits, both you and your referred friend must have complete accounts with verified emails.
          </p>
          {stats.pendingVerification > 0 && (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  {stats.pendingVerification} referral{stats.pendingVerification !== 1 ? 's' : ''} pending verification
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  These referrals are waiting for account completion. Credits will be awarded automatically once both accounts are verified.
                </p>
              </div>
            </div>
          )}
          {stats.pendingCredits > 0 && (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-start gap-3 mt-3">
              <Award className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-900">
                  {stats.pendingCredits} credits ready to be awarded!
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Your referrals have completed verification and credits will be awarded shortly.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-8 h-8 text-cyan-500" />
              <h3 className="text-lg font-bold text-gray-900">Total Referrals</h3>
            </div>
            <div className="text-4xl font-bold text-gray-900">{stats.totalReferrals}</div>
            <p className="text-sm text-gray-600 mt-1">Friends invited</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="w-8 h-8 text-green-500" />
              <h3 className="text-lg font-bold text-gray-900">Verified</h3>
            </div>
            <div className="text-4xl font-bold text-gray-900">{stats.verifiedReferrals}</div>
            <p className="text-sm text-gray-600 mt-1">Complete accounts</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-8 h-8 text-blue-500" />
              <h3 className="text-lg font-bold text-gray-900">Subscriptions</h3>
            </div>
            <div className="text-4xl font-bold text-gray-900">{stats.subscriptionReferrals}</div>
            <p className="text-sm text-gray-600 mt-1">Premium upgrades</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50">
            <div className="flex items-center gap-3 mb-2">
              <Award className="w-8 h-8 text-orange-600" />
              <h3 className="text-lg font-bold text-gray-900">Credits Earned</h3>
            </div>
            <div className="text-4xl font-bold text-orange-600">{stats.totalCreditsEarned}</div>
            <p className="text-sm text-gray-700 mt-1">Total rewards</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Referral History</h3>

          {referrals.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg font-semibold">No referrals yet</p>
              <p className="text-gray-500 text-sm mt-2">Share your referral code to start earning credits</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 font-bold text-gray-700">Friend</th>
                    <th className="text-left py-3 px-4 font-bold text-gray-700">Connection</th>
                    <th className="text-left py-3 px-4 font-bold text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 font-bold text-gray-700">Signup Date</th>
                    <th className="text-right py-3 px-4 font-bold text-gray-700">Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((referral) => {
                    // Defensive null checks
                    if (!referral) return null;

                    const signupCredits = referral.signup_completed_at ? 25 : 0;
                    const subscriptionCredits = referral.subscription_completed_at ? 75 : 0;
                    const totalCredits = signupCredits + subscriptionCredits;

                    // Safe access with fallback text
                    const refereeName = referral.referee?.full_name || 'New User';
                    const refereeEmail = referral.referee?.email || 'No email provided';

                    return (
                      <tr key={referral.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="py-3 px-4">
                          <div className="font-semibold text-gray-900">{refereeName}</div>
                          <div className="text-sm text-gray-600">{refereeEmail}</div>
                          {referral.referrer_contact && (
                            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Found via: {referral.referrer_contact}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-semibold text-gray-900">
                            {getConnectionReasonLabel(referral.connection_reason, referral.connection_reason_other)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col gap-1">
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold w-fit ${
                                referral.both_accounts_verified
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {referral.both_accounts_verified ? <ShieldCheck className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                              {referral.both_accounts_verified ? 'Verified' : 'Pending Verification'}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold w-fit ${
                                referral.signup_completed_at
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {referral.signup_completed_at ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                              Signed Up
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold w-fit ${
                                referral.subscription_completed_at
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {referral.subscription_completed_at ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                              Subscribed
                            </span>
                          </div>
                          {!referral.both_accounts_verified && (
                            <div className="mt-2 text-xs text-gray-600">
                              {!referral.referrer_account_complete && <div>Your account needs completion</div>}
                              {!referral.referee_account_complete && <div>Friend's account needs completion</div>}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(referral.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {referral.both_accounts_verified ? (
                            <span className="text-lg font-bold text-green-600">+{totalCredits}</span>
                          ) : (
                            <span className="text-sm font-semibold text-amber-600">Pending</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
